import type { ChangeCommitmentAndStateCommandData } from '@festina/life-general-types/life'
import { assertNotEmpty, notEmpty, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import {
  FormikDatePicker,
  FormikFieldArray,
  FormikForm,
} from '@festina-life/ui/formikForms'
import { DatePicker, FieldSet } from '@festina-life/ui/forms'
import { useMemo, useState } from 'react'

import { useComponent, useHook } from '../../lenteHooks'
import type { CommandDataFormProps } from '../commandActions/CommandDataFormAndSimulation'
import { ChangeCommitmentReviewModal } from './ChangeCommitmentReviewModal'
import { CoverEdits } from './CoverEdits'
import { PolicyEdits } from './PolicyEdits'

const validateKey =
  typeUtils.createValidator<ChangeCommitmentAndStateCommandData>()

// @beta None of the autogenerated fields are translated, so the ones here aren't either.
export function ChangeCommitmentForm({
  onSubmit,
  onStatusChanged,
  commandActionVariant,
  ...props
}: CommandDataFormProps<ChangeCommitmentAndStateCommandData>) {
  const CommitmentCommandDataFields = useComponent(
    'pages.commitmentEditor.CommitmentCommandDataFields',
  )
  const CommitmentFields = useComponent(
    'pages.commitmentEditor.CommitmentFields',
  )
  const CommitmentStateFields = useComponent(
    'pages.commitmentEditor.CommitmentStateFields',
  )

  assertNotEmpty(CommitmentCommandDataFields)
  assertNotEmpty(CommitmentFields)
  assertNotEmpty(CommitmentStateFields)

  const [reviewingCommand, setReviewingCommand] =
    useState<ChangeCommitmentAndStateCommandData>()

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { data: events } = useHook(
    'fetchers.life.useFetchCommitmentSourcesApplications',
  )({
    tenantId,
    ownerId,
  })

  // After rendering the form, changes to the `initialValues` won't update the fields.
  // Wait until a `changeDate` is selected to determine the correct commitment state
  // to use for the rest of the command.
  const [changeDate, setChangeDate] = useState(
    props.commandData?.changeDate ?? undefined,
  )
  const [isChangeDateConfirmed, setIsChangeDateConfirmed] = useState(
    commandActionVariant === 'create' ? false : true,
  )

  const { commitmentId, commitmentStateId } = useMemo(() => {
    if (!changeDate || !isChangeDateConfirmed) {
      return {}
    }

    const nonCancelledResponses =
      events?.commitmentEventResponses
        ?.filter((response) => !response?.commitmentCommand?.cancellationId)
        .reverse() ?? []

    let commitmentId = undefined
    let commitmentStateId = undefined

    for (const response of nonCancelledResponses) {
      if (
        response?.commitmentEvent?.applyDate &&
        // Find the refs right at or before the selected date
        response?.commitmentEvent?.applyDate <= changeDate
      ) {
        if (!commitmentId) {
          commitmentId = response.commitmentEvent.commitmentRef?._id
        }

        if (!commitmentStateId) {
          commitmentStateId = response.commitmentEvent.commitmentStateRef?._id
        }

        // Not all `commitmentEvent` seem to have a `commitmentRef`.
        // Most likely, a new version is only created when it has changes.
        // So we need to keep looping until we have both.
        if (commitmentId && commitmentStateId) {
          break
        }
      }
    }

    if (!commitmentId) {
      // If none of the events are at or before the selected date,
      // then the closest we can get is by using the oldest event.
      // The oldest event *should* always have both non-null refs.
      commitmentId =
        nonCancelledResponses.findLast(notEmpty)?.commitmentEvent?.commitmentRef
          ?._id
    }

    if (!commitmentStateId) {
      commitmentStateId =
        nonCancelledResponses.findLast(notEmpty)?.commitmentEvent
          ?.commitmentStateRef?._id
    }

    assertNotEmpty(commitmentId)
    assertNotEmpty(commitmentStateId)

    return {
      commitmentId,
      commitmentStateId,
    }
  }, [changeDate, events?.commitmentEventResponses, isChangeDateConfirmed])

  const { data: commitment } = useHook('fetchers.life.useFetchCommitment')(
    commitmentId
      ? {
          tenantId,
          ownerId,
          commitmentId,
        }
      : null,
  )

  const { data: commitmentState } = useHook(
    'fetchers.life.useFetchCommitmentState',
  )(
    commitmentStateId
      ? {
          tenantId,
          ownerId,
          commitmentStateId,
        }
      : null,
  )

  const initialValues = useMemo(
    () =>
      ({
        _type: 'ChangeCommitmentAndStateCommandData',
        changeDate,
        commitment,
        commitmentState,
      }) satisfies ChangeCommitmentAndStateCommandData,
    [changeDate, commitment, commitmentState],
  )

  // Not using <ChangeCommitmentAndStateCommandDataComponent> directly
  // because we want to put the top-level elements under disclosures.

  return (
    <>
      {reviewingCommand && (
        <ChangeCommitmentReviewModal
          initialValues={initialValues}
          command={reviewingCommand}
          onSubmit={onSubmit}
          onStatusChanged={onStatusChanged}
          onClose={() => setReviewingCommand(undefined)}
        />
      )}
      {!isChangeDateConfirmed && commandActionVariant === 'create' && (
        <>
          <DatePicker
            wrapperProps={{
              // See beta-comment above; lack of translation is intentional
              label: 'Change date',
              description: 'When the command should be applied',
            }}
            onChange={setChangeDate}
            value={changeDate}
          />
          <Button
            onClick={() => {
              setIsChangeDateConfirmed(true)
            }}
            disabled={!changeDate}
            // See beta-comment above; lack of translation is intentional
          >
            Confirm
          </Button>
        </>
      )}
      {commitment && commitmentState && (
        <FormikForm
          initialValues={initialValues}
          onSubmit={
            commandActionVariant === 'create' ? setReviewingCommand : onSubmit
          }
          {...props}
        >
          <FormikDatePicker
            // See beta-comment above; lack of translation is intentional
            label="Change date"
            name={validateKey('changeDate')}
            description="When the command should be applied"
            disabled
          />
          <CommitmentCommandDataFields />
          <FieldSet label="Commitment" isInitialOpen={false}>
            <CommitmentFields fieldNamePrefix={validateKey('commitment')} />
          </FieldSet>

          <FieldSet label="Commitment state" isInitialOpen={false}>
            <CommitmentStateFields
              fieldNamePrefix={validateKey('commitmentState')}
            />
          </FieldSet>
          <FieldSet label="Policies" isInitialOpen={false}>
            <FormikFieldArray
              name={validateKey('policyEdits')}
              // See beta-comment above; lack of translation is intentional
              label="Policy edits"
              elementLabel="Policy edits"
              elementInitializer={() => ({})}
              ChildElement={PolicyEdits}
              childProps={{
                commitment,
                commitmentState,
              }}
            />
          </FieldSet>
          <FieldSet label="Covers" isInitialOpen={false}>
            <FormikFieldArray
              name={validateKey('coverEdits')}
              // See beta-comment above; lack of translation is intentional
              label="Cover edits"
              elementLabel="Cover edits"
              elementInitializer={() => ({})}
              ChildElement={CoverEdits}
              childProps={{
                commitment,
                commitmentState,
              }}
            />
          </FieldSet>
        </FormikForm>
      )}
    </>
  )
}
